import type { components } from "@majitap/sdk";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";
import type { Tone } from "./labels";

type Schemas = components["schemas"];
export type Payment = Schemas["StaffPayment"];
export type PaymentRecord = Schemas["StaffPaymentRecord"];
export type TillTransaction = Schemas["TillTransaction"];
export type PaymentState = Payment["status"];
export type PaymentMethod = Payment["method"];
export type PaymentPurpose = Payment["purpose"];

export const PAYMENT_STATE: Record<PaymentState, [label: string, tone: Tone]> = {
  pending: ["Pending", "neutral"],
  processing: ["Waiting for M-Pesa", "info"],
  succeeded: ["Succeeded", "success"],
  failed: ["Failed", "danger"],
  cancelled: ["Cancelled", "neutral"],
};

export const METHOD: Record<PaymentMethod, string> = {
  mpesa: "M-Pesa",
  card: "Card",
  bank_transfer: "Bank transfer",
  cash: "Cash",
  cheque: "Cheque",
  wallet: "Wallet",
};

export const PURPOSE: Record<PaymentPurpose, string> = {
  order: "Order",
  invoice: "Invoice",
  refund: "Refund",
  payout: "Partner payout",
  wallet_top_up: "Wallet top-up",
};

/** What the payment was for: till money matched to a payer's account carries no order. */
export const purposeOf = (p: Pick<Payment, "purpose" | "order_id">) =>
  p.purpose === "order" && !p.order_id ? "On account" : PURPOSE[p.purpose];

/**
 * Refund is offered with `payments.refund`, on a payment the API says can still be refunded
 * (`refundable` above zero). The API decides what's refundable; the console never works it out.
 */
export const canRefund = (p: Pick<Payment, "refundable">, permissions: string[]) =>
  permissions.includes("payments.refund") && p.refundable !== null && !/^0+(\.0+)?$/.test(p.refundable);

export const paymentQuery = (id: string) =>
  queryOptions({
    queryKey: ["payment", id],
    queryFn: () => ok(api.GET("/v1/admin/payments/{id}", { params: { path: { id } } })),
  });

/** What a till payment can be matched to: exactly one of these. */
export type MatchTarget =
  | { invoice_id: string }
  | { order_id: string }
  | { customer_id: string }
  | { business_id: string };

export type RecordPayment = Schemas["RecordPaymentBody"];
export type RecordMethod = RecordPayment["method"];

/** How staff can say money came in outside the app (P8-39). */
export const RECORD_METHOD: Record<RecordMethod, { label: string; reference: string }> = {
  cash: { label: "Cash", reference: "Receipt number" },
  bank_transfer: { label: "Bank transfer", reference: "Transfer reference" },
  cheque: { label: "Cheque", reference: "Cheque number" },
  mpesa: { label: "M-Pesa, sent to a number (not the till)", reference: "M-Pesa receipt code" },
};

export const paymentActions = {
  /** Money received outside the app; the key makes a retried submit record it once. */
  record: (body: RecordPayment, key: string) =>
    ok(api.POST("/v1/admin/payments/record", { params: { header: { "idempotency-key": key } }, body })),
  /** Prompt the customer's phone to pay an order. */
  stk: (body: { order_id: string; phone_number: string }, key: string) =>
    ok(api.POST("/v1/admin/payments/mpesa/stk", { params: { header: { "idempotency-key": key } }, body })),
  match: (tillId: string, target: MatchTarget) =>
    ok(api.POST("/v1/admin/payments/till/{id}/match", { params: { path: { id: tillId } }, body: target })),
  refund: (id: string, body: { amount: string; reason: string; to: "wallet" | "manual" }) =>
    ok(api.POST("/v1/admin/payments/{id}/refund", { params: { path: { id } }, body })),
};
